import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AddonDTO, SubscriptionDTO } from '../../Types/Billing';
import { ChargebeeS } from '../ChargebeeS';
import SubscriptionS from '../restapi/subscriptionService';
import { BookitupScope } from '../ProfileS';
import { useJwtClaims } from './AuthContext';

interface BookitupSubscriptionContextInterface {
  subscription?: SubscriptionDTO;
  isActive: boolean;
  resourceAddon: AddonDTO | null;
  teamAddonPresent: boolean;
  refresh: () => void;
  loading: boolean;
  openPortal: () => void;
}

type BookitupScopeSelection = {
  isOwner: boolean;
  isScopeSelected: boolean;
};

export const BookitupSubscriptionContext = createContext<BookitupSubscriptionContextInterface>(
  {} as BookitupSubscriptionContextInterface,
);

const normalizeAddonName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const RESOURCE_ADDON_NAMES = ['Resource-Planning', 'Ressourcen'];
const TEAM_ADDON_NAMES = ['Team'];

const findAddon = (addons: AddonDTO[] | undefined, name: string) => {
  if (!addons || addons.length === 0) return null;
  const normalizedName = normalizeAddonName(name);
  return addons.find((addon) => normalizeAddonName(addon.name).startsWith(normalizedName)) ?? null;
};

const findResourceAddon = (addons: AddonDTO[] | undefined) =>
  RESOURCE_ADDON_NAMES.map((addonName) => findAddon(addons, addonName)).find(Boolean) ?? null;

const hasTeamAddon = (addons: AddonDTO[] | undefined) =>
  TEAM_ADDON_NAMES.some((addonName) => findAddon(addons, addonName));

export const BookitupSubscriptionProvider = () => {
  const [subscription, setSubscription] = useState<SubscriptionDTO>();
  const [loading, setLoading] = useState(false);
  const { profileId } = useJwtClaims();
  const { addons, status } = subscription ?? {};
  const resourceAddon = findResourceAddon(addons);
  const teamAddonPresent = hasTeamAddon(addons);
  const isActive = status === 'ACTIVE';

  const fetchSubscriptionDetails = useCallback(() => {
    setLoading(true);
    SubscriptionS.getSubscription()
      .then((resp) => {
        if (resp.ok) {
          resp.json().then(setSubscription);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (profileId) fetchSubscriptionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const openPortal = () => ChargebeeS.openPortal(fetchSubscriptionDetails);

  const value = useMemo(
    () => ({
      loading,
      resourceAddon,
      teamAddonPresent,
      isActive,
      refresh: fetchSubscriptionDetails,
      subscription,
      openPortal,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, subscription, resourceAddon, teamAddonPresent],
  );

  return (
    <BookitupSubscriptionContext.Provider value={value}>
      <Outlet />
    </BookitupSubscriptionContext.Provider>
  );
};

export const useBookitupSubscription = () => useContext(BookitupSubscriptionContext);

export const useAddon = (name: string) => {
  const { subscription } = useBookitupSubscription();
  const { addons } = subscription ?? { addons: [] };
  const addon = findAddon(addons, name) ?? undefined;
  return { isPresent: addon !== undefined, addon };
};

export const useBookitupScope = (scopeOfInterest: BookitupScope): BookitupScopeSelection => {
  const { role, scopes } = useJwtClaims();
  const { teamAddonPresent } = useBookitupSubscription();

  return useMemo(() => {
    const isOwner = !teamAddonPresent || !role || role === 'OWNER';
    const isScopeSelected = isOwner || scopes?.includes(scopeOfInterest);

    return {
      isOwner,
      scopes,
      isScopeSelected,
    };
  }, [role, scopes, scopeOfInterest, teamAddonPresent]);
};
